import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, Grid, makeStyles } from '@material-ui/core';
import { colors, DefaultButton, useFormHook } from 'design-system';
import { useEffect, useMemo, useState } from 'react';
// Redux
import { connect, ConnectedProps } from 'react-redux';
import { setLegalBeneficiaries } from 'src/store/emissionPolicy/actions';
import { RootState } from 'src/store/rootReducer';
// Models
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { LegalBeneficiary } from 'src/models/helperTypes/EmissionDataFields';
import PersonDataResponse from 'src/models/responseTypes/PersonValidationResponse';
import { requestSearchValidateBeneficiaryPerson } from 'src/store/person/actions';
import { REQUEST_SEARCH_VALIDATE_BENEFICIARY_PERSON } from 'src/store/person/requestIdentifiers';
import {
  deleteBenefiactyDataOnForm,
  parseData,
  parseInitialFormData,
} from './dataMethods';
// import FormShape from './FormShape'
import makeBeneficiariesValidationSchema from './makeBeneficiariesValidationSchema';
// Components
import PersonValidationAlert from '../../../components/PersonValidationAlert';
import PersonValidationModal from '../../../components/PersonValidationModal';
import BeneficiaryAlerts from './BeneficiaryAlerts';
import { BeneficiaryInputs } from './BeneficiaryInputs';
import BeneficiaryPercentageCard from './BeneficiaryPercentageCard';
import FormShape from './FormShape';

const useStyles = makeStyles(() => {
  return createStyles({
    form: {
      paddingRight: 10,
      paddingLeft: 10,
      paddingTop: 20,
    },
  });
});

type PercentageControl = {
  total: number;
  over: boolean;
  under: boolean;
  equal: boolean;
};

const defaultError = { error: false, errorText: '' };

export const BeneficiaryForm = ({
  onSubmit,
  personValidateData,
  legalBeneficiaries,
  setLegalBeneficiariesAction,
  validatePersonAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [openValidationModal, setOpenValidationModal] = useState(false);
  const [currentForm, setCurrentForm] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ error: boolean; errorText: string }[]>(
    legalBeneficiaries && legalBeneficiaries.length > 0
      ? legalBeneficiaries.map(() => defaultError)
      : [defaultError],
  );

  const handleFormSubmit = (data: FormShape): void => {
    setLegalBeneficiariesAction(parseData(data));
    onSubmit();
  };

  const { getInputProps, handleSubmit, updateMultipleInputs } =
    useFormHook<FormShape>(
      parseInitialFormData(legalBeneficiaries),
      handleFormSubmit,
      makeBeneficiariesValidationSchema(),
      { deferErrorCheck: true },
    );

  const totalBeneficiaries = getInputProps('totalBeneficiaries')
    .value as number;

  const helper = new Array(
    getInputProps('totalBeneficiaries').value as number,
  ).fill(0);

  const { equal, over, total, under } = useMemo((): PercentageControl => {
    let accumulator = 0;

    const helperArray = new Array(
      getInputProps('totalBeneficiaries').value as number,
    ).fill(0);
    helperArray.forEach((_, index) => {
      const percentage = getInputProps(`beneficiary${index}participationRate`)
        .value as number;
      const subTotal = Number(percentage);
      accumulator += subTotal;
    });
    return {
      total: accumulator,
      equal: accumulator === 100,
      under: accumulator < 100,
      over: accumulator > 100,
    };
  }, [getInputProps]);

  const handleDeleteBeneficiary = (index: number): void => {
    updateMultipleInputs(deleteBenefiactyDataOnForm(getInputProps, index));
  };

  const requestingPerson = useNetworkObserver(
    REQUEST_SEARCH_VALIDATE_BENEFICIARY_PERSON,
  );

  const handleAddBeneficiary = (): void => {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors.push({
        error: false,
        errorText: '',
      });
      return newErrors;
    });
    getInputProps('totalBeneficiaries').onChange(totalBeneficiaries + 1);
  };

  const handleViewModal = (): void => {
    setOpenValidationModal(!personValidateData.isValidPerson);
    setShowValidationAlert(!personValidateData.isValidPerson);
    if (loading) setLoading(false);
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      if (
        !personValidateData.isValidPerson ||
        (personValidateData.personExist && !personValidateData.personInnova)
      ) {
        newErrors[currentForm] = {
          error: !personValidateData.isValidPerson,
          errorText: 'Ingrese una identificación válida',
        };
      }
      return newErrors;
    });
  };

  useEffect(() => {
    if (requestingPerson === 'success') {
      handleViewModal();
    }
  }, [personValidateData, requestingPerson]);

  return (
    <form
      data-testid="benificiary-form"
      onSubmit={handleSubmit}
      className={classes.form}
    >
      <PersonValidationModal
        data-testid="person-validation-modal"
        onClose={() => setOpenValidationModal(false)}
        open={openValidationModal}
      />
      <Grid container justifyContent="center">
        <Grid
          container
          spacing={3}
          item
          xs={12}
          md={9}
          lg={5}
          justifyContent="center"
          alignItems="flex-start"
        >
          <Grid item xs={12}>
            <PersonValidationAlert
              data-testid="person-validation-alert"
              onCloseAlert={() => setShowValidationAlert(false)}
              showAlert={showValidationAlert}
            />
          </Grid>
          <Grid item xs={12}>
            <BeneficiaryAlerts isEqual={equal} isOver={over} isUnder={under} />
          </Grid>

          <Grid item xs={12}>
            <BeneficiaryPercentageCard totalParticipation={total} />
          </Grid>

          {helper.map((_, index) => (
            <Grid item xs={12} key={`beneficiary-input-${index + 1}`}>
              <BeneficiaryInputs
                data-testid={`beneficiary-input-${index + 1}`}
                getInputProps={getInputProps}
                index={index}
                setErrors={setErrors}
                errors={errors}
                setCurrentForm={setCurrentForm}
                currentForm={currentForm}
                showPercentageError={over}
                validatePersonAction={validatePersonAction}
                onDeleteBeneficiary={handleDeleteBeneficiary}
                totalBeneficiaries={totalBeneficiaries}
                setLoading={setLoading}
              />
            </Grid>
          ))}

          <Grid item container xs={12} justifyContent="center">
            <Grid item xs={12} md={8} lg={6}>
              <DefaultButton
                id="sales-web-inspection-beneficiaries-save"
                data-testid="add-button"
                type="button"
                variant="borderless"
                fullWidth
                disabled={totalBeneficiaries === 5 || loading}
                onClick={handleAddBeneficiary}
              >
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  fontSize={24}
                  color={
                    totalBeneficiaries === 5
                      ? colors.light_silver
                      : colors.bright_turquoise
                  }
                />{' '}
                &nbsp;&nbsp; agregar beneficiario
              </DefaultButton>
            </Grid>
          </Grid>

          <Grid item container xs={12} justifyContent="center">
            <Grid item xs={12} md={8} lg={6}>
              <DefaultButton
                id="sales-web-inspection-beneficiaries-save"
                data-testid="submit-button"
                fullWidth
                type="submit"
                disabled={
                  !equal || errors.some((error) => error.error) || loading
                }
              >
                Guardar
              </DefaultButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  legalBeneficiaries?: LegalBeneficiary[];
  personValidateData: PersonDataResponse;
} => ({
  legalBeneficiaries: state.emissionPolicy.legalBeneficiaries,
  personValidateData: state.personValidate.beneficiaryPersonValidateData,
});

const reduxActions = {
  setLegalBeneficiariesAction: setLegalBeneficiaries,
  validatePersonAction: requestSearchValidateBeneficiaryPerson,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(BeneficiaryForm);
